import type { Metrics, OrderItemRow } from "~/store/restock/restockOrderSlice";

const computeMetrics = (orderItems: OrderItemRow[]): Metrics => {
	// Group rows by itemId so per-item inventory fields (`available`, `availableValue`)
	// contribute once per item. These are keyed by ItemId in wsapi and mirrored to every
	// (item, customer) row on the frontend — summing them across rows inflates by N rows/item.
	// Per-(item, customer) fields (`orderQuantity`, `allocatedValue`, `carts`) continue to sum
	// across all rows. Mirrors the grouping idiom in `buildItemTotals.ts`.
	const byItem = new Map<string, OrderItemRow[]>();
	for (const row of orderItems) {
		const existing = byItem.get(row.itemId);
		if (existing) existing.push(row);
		else byItem.set(row.itemId, [row]);
	}

	let totalAvailable = 0;
	let costOfAvailable = 0;
	let totalInTransit = 0;
	let totalOrderQty = 0;
	let totalRemaining = 0;
	for (const rows of byItem.values()) {
		const head = rows[0];
		const itemAvailable = head.available;
		const itemInTransit = Number(head.inTransit);
		const itemOrderQty = rows.reduce((s, r) => s + Number(r.orderQuantity), 0);
		totalAvailable += itemAvailable;
		costOfAvailable += Number(head.availableValue);
		totalInTransit += itemInTransit;
		totalOrderQty += itemOrderQty;
		// Mirrors load list's `remainingAvailability = available − inTransit − allocated`.
		// Intentionally unclamped: negative remaining signals over-allocation to the user.
		totalRemaining += itemAvailable - itemInTransit - itemOrderQty;
	}

	const totalCarts = orderItems.reduce((sum, item) => sum + Number(item.carts), 0);
	// Compute costOfAllocated locally from unitValue × orderQuantity instead of reading backend
	// `allocatedValue`. Optimistic overrides mutate `orderQuantity` but not `allocatedValue`, so
	// sourcing the $ metric from the backend field drifts stale until refetch while the quantity
	// metrics update instantly — producing inconsistent sidebar state mid-edit.
	const costOfAllocated = orderItems.reduce(
		(sum, item) => sum + Number(item.unitValue) * Number(item.orderQuantity),
		0
	);
	// Intentionally unclamped: >100% signals over-allocation.
	// Rounding only — guarded by totalAvailable > 0 to avoid divide-by-zero.
	const allocationPercent = totalAvailable > 0 ? (totalOrderQty / totalAvailable) * 100 : 0;
	// Session-level remaining $ derived algebraically from the two deduped totals:
	// costOfAvailable is per-item (deduped above) and costOfAllocated is per-row (locally computed).
	// Derivation keeps the session aggregate correct, stays in sync with optimistic overrides, and
	// needs no fragment change.
	const totalRemainingValue = costOfAvailable - costOfAllocated;

	return {
		allocationPercent: Math.round(allocationPercent * 10) / 10,
		costOfAllocated,
		costOfAvailable,
		totalAvailable,
		totalCarts,
		totalInTransit,
		totalRemaining,
		totalRemainingValue
	};
};

export default computeMetrics;
